// src/pages/WithdrawnBooksPage.tsx
// 「除籍済みの本」管理画面。書誌管理ページ最下部の「除籍済みの本」ボタンから遷移する。
// 除籍日が新しい順の表で書影・タイトル・除籍日を表示し、行末の3点メニューから
// 「除籍を取り消す」「完全に削除する」を選べる。

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import {
  getWithdrawnBooks,
  restoreWithdrawnBook,
  deleteBookPermanently,
  WithdrawnBook,
} from '../utils/bibliographyApi';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const titleWithVolume = (title?: string, volume?: string | number | null): string => {
  const t = title ?? '';
  const v = volume != null ? String(volume).trim() : '';
  return v ? `${t}（${v}）` : t;
};

const formatWithdrawnAt = (raw: string): string => {
  try {
    const d = new Date(raw.replace(' ', 'T'));
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return raw;
  }
};

const WithdrawnBooksPage: React.FC = () => {
  const { idToken } = useAuth();
  const TOKENS = useAppTokens();

  const [books, setBooks] = useState<WithdrawnBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const [menuState, setMenuState] = useState<{ anchor: HTMLElement; book: WithdrawnBook } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WithdrawnBook | null>(null);

  const load = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const { items, nextCursor: next } = await getWithdrawnBooks(idToken);
      setBooks(items);
      setNextCursor(next);
    } catch (e) {
      setError('除籍済みの本の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLoadMore = async () => {
    if (!idToken || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, nextCursor: next } = await getWithdrawnBooks(idToken, nextCursor);
      setBooks((prev) => [...prev, ...items]);
      setNextCursor(next);
    } catch (e) {
      setError('続きの取得に失敗しました。');
    } finally {
      setLoadingMore(false);
    }
  };

  const closeMenu = () => setMenuState(null);

  const handleRestore = async (book: WithdrawnBook) => {
    if (!idToken) return;
    closeMenu();
    setProcessingId(book.global_book_id);
    setError(null);
    try {
      await restoreWithdrawnBook(book.global_book_id, idToken);
      setBooks((prev) => prev.filter((b) => b.global_book_id !== book.global_book_id));
    } catch (e) {
      setError('除籍の取り消しに失敗しました。');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!idToken || !deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setProcessingId(target.global_book_id);
    setError(null);
    try {
      await deleteBookPermanently(target.global_book_id, idToken);
      setBooks((prev) => prev.filter((b) => b.global_book_id !== target.global_book_id));
    } catch (e) {
      setError('完全削除に失敗しました。');
    } finally {
      setProcessingId(null);
    }
  };

  const cellSx = { color: TOKENS.text, borderColor: TOKENS.border } as const;

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 6 }}>
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Button
          component={Link}
          to="/bibliography"
          sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}
        >
          ← 書誌管理
        </Button>

        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 2.5 }}>除籍済みの本</Typography>

        {error && (
          <Typography sx={{ color: TOKENS.error, fontSize: 13, mb: 2 }}>{error}</Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : books.length === 0 ? (
          <Typography sx={{ color: TOKENS.textSub, fontSize: 13, textAlign: 'center', mt: 4 }}>
            除籍済みの本はありません。
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ border: `1px solid ${TOKENS.border}`, background: TOKENS.glass, borderRadius: '14px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...cellSx, width: 56 }} />
                    <TableCell sx={cellSx}>タイトル</TableCell>
                    <TableCell sx={cellSx}>除籍日</TableCell>
                    <TableCell sx={{ ...cellSx, width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {books.map((book) => (
                    <TableRow key={book.global_book_id}>
                      <TableCell sx={cellSx}>
                        <Box
                          component="img"
                          src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                          alt={book.title ?? ''}
                          sx={{ width: 36, height: 50, objectFit: 'contain', borderRadius: '4px', background: TOKENS.cardBg }}
                        />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                          {titleWithVolume(book.title, book.volume)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                          {formatWithdrawnAt(book.global_book_withdrawn_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {processingId === book.global_book_id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => setMenuState({ anchor: e.currentTarget, book })}
                            sx={{ color: TOKENS.textSub }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  variant="outlined"
                  sx={{
                    color: TOKENS.text,
                    borderColor: TOKENS.border,
                    textTransform: 'none',
                    '&:hover': { borderColor: TOKENS.accent, background: TOKENS.cardBgHover },
                  }}
                >
                  {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  もっと見る
                </Button>
              </Box>
            )}
          </>
        )}

        <Menu anchorEl={menuState?.anchor} open={!!menuState} onClose={closeMenu}>
          <MenuItem onClick={() => menuState && handleRestore(menuState.book)}>除籍を取り消す</MenuItem>
          <MenuItem
            onClick={() => {
              if (menuState) setDeleteTarget(menuState.book);
              closeMenu();
            }}
            sx={{ color: TOKENS.error }}
          >
            完全に削除する
          </MenuItem>
        </Menu>

        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
          <DialogTitle>完全に削除しますか？</DialogTitle>
          <DialogContent>
            <DialogContentText>
              「{titleWithVolume(deleteTarget?.title, deleteTarget?.volume)}」を完全に削除します。この操作は取り消せません。
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>キャンセル</Button>
            <Button onClick={handleDeleteConfirmed} sx={{ color: TOKENS.error }}>
              完全に削除する
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default WithdrawnBooksPage;
